// src/pages/Chat.tsx
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChatRichText } from "../components/chat/ChatRichText";
import {
  api,
  ChatHistoryItem,
  type NewsJobResponse,
  type NewsJobStatusData,
  type NewsDeliveryOption,
  type NewsNotificationType,
  UserData,
} from "../lib/api";
import { resolveBackendAssetUrl } from "../lib/backendAsset";
import { resolveNewsGoogleReconsentCompleteUrl } from "../lib/config";
import { getOrCreateDeviceId } from "../lib/device"; // UUID生成は device.ts に集約
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  normalizeGoogleIntegrationStatus,
  type GoogleIntegrationView,
} from "../lib/googleIntegration";
import { GOOGLE_SCOPE_BUNDLES } from "../lib/googleScopes";
import {
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromAccountState,
  writeLineLinkSnapshot,
  type LineLinkSnapshot,
} from "../lib/lineLink";
import {
  clearPendingNewsJobSnapshot,
  createPendingNewsJobSnapshot,
  getDefaultNewsNotificationType,
  getNewsNotificationTypeLabel,
  NEWS_PENDING_JOB_EVENT,
  readPendingNewsJobSnapshot,
  resolveNewsNotificationType,
  toPendingNewsJobNotification,
  type PendingNewsJobSnapshot,
  writePendingNewsJobNotification,
  writePendingNewsJobSnapshot,
} from "../lib/newsJob";
import {
  clearPendingNewsGoogleReconsentSnapshot,
  normalizeNewsGoogleAuthorizationUrl,
  writePendingNewsGoogleReconsentSnapshot,
} from "../lib/newsGoogleReconsent";
import {
  NEWS_GMAIL_SEND_SCOPE_BUNDLE,
  resolveNewsJobConflictDisplay,
} from "../lib/newsJobError";
import { getByPath, pickStringByPaths } from "../lib/path";
import { storage } from "../lib/storage";

type Message = {
  id: string;
  seq?: number;
  from: "character" | "user";
  text: string;
  time: string; // "HH:mm"
  date?: string; // "YYYY-MM-DD"
  isTyping?: boolean; // タイピングインジケーター用
};

const CHAT_HISTORY_PAGE_SIZE = 30;
const MAX_MESSAGE_LENGTH = 600;
const SEND_COOLDOWN_MS = 1000;

function formatTimeFromDateString(dateStr?: string): string {
  if (!dateStr) return "";
  return dateStr.length >= 16 ? dateStr.slice(11, 16) : "";
}

function formatDateFromDateString(dateStr?: string): string {
  if (!dateStr) return "";
  return dateStr.length >= 10 ? dateStr.slice(0, 10) : "";
}

function nowHHMM(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function nowYYYYMMDD(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * ランダムID（永続化しない用途）
 * - crypto.randomUUID は使わず、deviceId + 時刻で衝突回避
 */
function makeRuntimeId(prefix = "m"): string {
  const base = typeof window === "undefined" ? `${Date.now()}` : getOrCreateDeviceId();
  return `${prefix}_${base}_${Date.now()}`;
}

function mapHistoriesToMessages(histories: ChatHistoryItem[]): Message[] {
  const sorted = [...histories].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  return sorted
    .filter((h) => typeof h?.text === "string" && h.text.length > 0)
    .map((h) => ({
      id: h.id,
      seq: h.seq,
      from: h.inout === 1 ? "character" : "user",
      text: h.text,
      time: formatTimeFromDateString(h.date),
      date: formatDateFromDateString(h.date),
    }));
}

function getOldestMessageSeq(messages: Message[]): number | null {
  for (const message of messages) {
    if (typeof message.seq === "number" && Number.isFinite(message.seq)) {
      return message.seq;
    }
  }
  return null;
}

function normalizeId(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function resolveCurrentCharacterId(user: UserData): string | null {
  return normalizeId(
    getByPath(user, "currentCharacter.characterId") ??
      getByPath(user, "currentCharacter.character_id") ??
      getByPath(user, "data.currentCharacter.characterId") ??
      getByPath(user, "data.currentCharacter.character_id") ??
      getByPath(user, "currentCharacter.id") ??
      getByPath(user, "data.currentCharacter.id") ??
      getByPath(user, "currentCharacterId") ??
      getByPath(user, "data.currentCharacterId")
  );
}

type ChatTalkPayload = {
  message?: string | null;
  speechFileUrl?: string | null;
  messages?: {
    id?: string | number | null;
    seq?: string | number | null;
    date?: string | null;
    inout?: string | number | null;
    text?: string | null;
  };
};

type ChatTalkEnvelope = {
  data?: ChatTalkPayload | null;
};

function isChatTalkEnvelope(res: ChatTalkPayload | ChatTalkEnvelope | null | undefined): res is ChatTalkEnvelope {
  return Boolean(res && typeof res === "object" && "data" in res);
}

function normalizeChatTalkPayload(res: ChatTalkPayload | ChatTalkEnvelope | null | undefined): ChatTalkPayload {
  if (isChatTalkEnvelope(res)) {
    return res.data ?? {};
  }
  return res ?? {};
}

type NewsJobFormState = {
  keyword: string;
  notificationType: NewsNotificationType;
  email: string;
  mode: "news" | "report";
  readingProfile: "grasp" | "organize" | "think";
  deliveryOption: NewsDeliveryOption;
};

type NewsGoogleReconsentAction = {
  authorizationUrl: string;
  requiredScopeBundle: string;
};

type InlineError = {
  message: string;
  detail?: string;
  newsGoogleReconsent?: NewsGoogleReconsentAction;
};

type NewsJobViewStatus = "accepted" | "polling" | "completed" | "failed" | "timeout";

type NewsJobView = {
  phase: NewsJobViewStatus;
  id: string;
  jobId: string;
  status: "accepted" | "completed" | "failed";
  keyword: string;
  notificationType: NewsNotificationType;
  acceptedAt?: string | null;
  processedAt?: string | null;
  failedStage?: string | null;
  errorMessage?: string | null;
  callbackMessage?: string | null;
  summary?: string | null;
};

type ChatRouteState = {
  reopenNewsJobModal?: boolean;
  newsJobForm?: Partial<NewsJobFormState>;
};

const INITIAL_NEWS_JOB_FORM: NewsJobFormState = {
  keyword: "",
  notificationType: "line",
  email: "",
  mode: "news",
  readingProfile: "think",
  deliveryOption: "text",
};

function resolveNewsDeliveryOption(value: unknown): NewsDeliveryOption {
  if (value === "audio" || value === "text_and_audio") {
    return value;
  }
  return "text";
}

const TEST_NEWS_CHAT_LABEL = getNewsNotificationTypeLabel("google_chat");

const NEWS_JOB_POLL_INTERVAL_MS = 2000;
const NEWS_JOB_POLL_TIMEOUT_MS = 60000;
const REPORT_JOB_POLL_TIMEOUT_MS = 300000;
const REFRESH_NEWS_JOB_STATUS_BUTTON_LABEL = "状態を確認";

function createLoadingGoogleIntegrationView(): GoogleIntegrationView {
  return {
    state: "loading",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function createErrorGoogleIntegrationView(): GoogleIntegrationView {
  return {
    state: "error",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function extractBackendMessage(error: unknown): string | undefined {
  if (!(error instanceof AppError)) return undefined;
  if (error.code !== "SERVER" || error.meta.status !== 502) return undefined;
  if (!error.meta.responseText) return undefined;

  try {
    const parsed = JSON.parse(error.meta.responseText);
    const message = typeof parsed?.message === "string" ? parsed.message.trim() : "";
    return message || undefined;
  } catch {
    return undefined;
  }
}

function toNewsUserFacingErrorMessage(message?: string | null): string {
  const normalized = (message ?? "").trim();
  if (!normalized) return "ニュース送信に失敗しました。";

  const lowered = normalized.toLowerCase();
  if (
    lowered.includes("valueerror") ||
    lowered.includes("did not include a json object") ||
    lowered.includes("gemini evidence bundle")
  ) {
    return "ニュース内容の生成に失敗しました。しばらくしてから再試行してください。";
  }

  return normalized;
}

function formatDateTime(value?: string | null): string {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getNewsJobHeadline(result: { phase?: string }): string {
  switch (result.phase) {
    case "accepted":
    case "polling":
      return "ニュースを送る準備を進めています";
    case "completed":
      return "ニュース送信が完了しました";
    case "failed":
      return "ニュース送信に失敗しました";
    case "timeout":
      return "状態確認に時間がかかっています";
    default:
      return "";
  }
}

function getNewsRequestGuideLines(snapshot: LineLinkSnapshot, notificationType: NewsNotificationType) {
  const acceptedResultGuide = "依頼後は受付ID・ステータスがこの画面に表示されます。";

  if (snapshot.status === "linked") {
    return [
      "キーワードを入力して通知先を選び、「依頼する」を押すとニュース送信ジョブを受け付けます。",
      notificationType === "line"
        ? "現在はLINE連携済みなので、このままLINEで受け取る依頼を送れます。"
        : `現在はLINE連携済みなので、${TEST_NEWS_CHAT_LABEL} と LINE のどちらでも依頼できます。`,
      acceptedResultGuide,
    ];
  }

  if (snapshot.status === "pending") {
    return [
      `キーワードを入力すると、${TEST_NEWS_CHAT_LABEL} 向けのニュース送信ジョブをこの画面から依頼できます。`,
      "LINEで受け取りたい場合は、先にLINE公式アカウントへの連携を完了してください。",
      acceptedResultGuide,
    ];
  }

  if (snapshot.status === "error") {
    return [
      `キーワードを入力すると、${TEST_NEWS_CHAT_LABEL} 向けのニュース送信ジョブをこの画面から依頼できます。`,
      "LINE通知を使うには、LINE連携をやり直して連携済みにしてください。",
      acceptedResultGuide,
    ];
  }

  return [
    `キーワードを入力すると、${TEST_NEWS_CHAT_LABEL} 向けのニュース送信ジョブをこの画面から依頼できます。`,
    "LINEで受け取りたい場合は、先にLINE連携を行ってください。",
    acceptedResultGuide,
  ];
}


/** ---- component ---- */

export default function Chat() {
  const navigate = useNavigate();
  const location = useLocation();
  const [characterName, setCharacterName] = useState<string>("");
  const [characterIconUrl, setCharacterIconUrl] = useState<string>("/img/no-image.png");
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());

  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [sendError, setSendError] = useState<string>("");
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(false);
  const [nextBeforeSeq, setNextBeforeSeq] = useState<number | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState<boolean>(false);
  const [loadOlderError, setLoadOlderError] = useState<string>("");
  const [historyScrollRestoreTick, setHistoryScrollRestoreTick] = useState(0);
  const [sending, setSending] = useState<boolean>(false);
  const sendingRef = useRef(false);
  const sendCooldownUntilRef = useRef<number>(0);

  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [speechEnabled, setSpeechEnabled] = useState(false); // 音声出力ON/OFF（デフォルト：OFF）
  const speechAudioRef = useRef<HTMLAudioElement | null>(null);
  const longPressTimerRef = useRef<number | null>(null); // 長押し検出用
  const voiceRecognitionRef = useRef<any>(null);
  const voiceSessionSentRef = useRef(false);
  const voiceActiveRef = useRef(false);
  const lastVoiceSentRef = useRef<{ text: string; at: number } | null>(null);
  const pendingVoiceTextRef = useRef<string | null>(null);
  const voiceSendTimerRef = useRef<number | null>(null);
  const voiceAccumulatedTextRef = useRef<string>("");
  const isIOSRef = useRef(false);
  const voiceRestartTimeoutRef = useRef<number | null>(null);
  const voiceLastFinalIndexRef = useRef<number>(-1);
  const voiceSoundActiveRef = useRef(false);
  const voiceHealthCheckIntervalRef = useRef<number | null>(null);

  const newsJobPollTimerRef = useRef<number | null>(null);
  const newsJobStartedAtRef = useRef<number | null>(null);
  const [newsJobModalOpen, setNewsJobModalOpen] = useState<boolean>(false);
  const [newsJobSubmitting, setNewsJobSubmitting] = useState<boolean>(false);
  const [, setNewsJobRefreshing] = useState<boolean>(false);
  const [newsJobError, setNewsJobError] = useState<InlineError | null>(null);
  const [newsJobResult, setNewsJobResult] = useState<NewsJobView | null>(null);
  const [newsGoogleIntegrationView, setNewsGoogleIntegrationView] = useState<GoogleIntegrationView>(
    createLoadingGoogleIntegrationView()
  );
  const [pendingNewsJob, setPendingNewsJob] = useState<PendingNewsJobSnapshot | null>(
    readPendingNewsJobSnapshot()
  );
  const canUseNewsEmail = newsGoogleIntegrationView.state === "connected";
  const [newsJobForm, setNewsJobForm] = useState<NewsJobFormState>(() => ({
    ...INITIAL_NEWS_JOB_FORM,
    notificationType: getDefaultNewsNotificationType(
      readLineLinkSnapshot().status === "linked",
      false
    ),
    email: storage.getNewsEmail() ?? "",
  }));

  const endRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const historyScrollSnapshotRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const skipNextAutoScrollRef = useRef(false);
  const didInitialScrollRef = useRef(false);

  // メッセージエリア自動フォーカス用
  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  const refreshNewsEmailIntegrationStatus = useCallback(async () => {
    try {
      const googleIntegrationStatus = await api.getGoogleIntegrationStatus(NEWS_GMAIL_SEND_SCOPE_BUNDLE);
      setNewsGoogleIntegrationView(normalizeGoogleIntegrationStatus(googleIntegrationStatus));
    } catch (googleIntegrationError) {
      logError(googleIntegrationError);
      setNewsGoogleIntegrationView(createErrorGoogleIntegrationView());
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as any;
    const supported = typeof w.SpeechRecognition === "function" || typeof w.webkitSpeechRecognition === "function";
    setVoiceSupported(supported);
    
    // iOS判定
    const ua = navigator.userAgent;
    isIOSRef.current = /iPhone|iPad|iPod/.test(ua);

    // 音声出力設定をlocalStorageから読み込み
    const saved = localStorage.getItem("wizmate_speech_enabled");
    if (saved !== null) {
      setSpeechEnabled(saved === "true");
    }
  }, []);

  useEffect(() => {
    const routeState =
      typeof location.state === "object" && location.state ? (location.state as ChatRouteState) : undefined;

    if (!routeState?.reopenNewsJobModal) return;

    setNewsJobError(null);
    setNewsJobForm({
      keyword: typeof routeState.newsJobForm?.keyword === "string" ? routeState.newsJobForm.keyword : "",
      notificationType: resolveNewsNotificationType(
        routeState.newsJobForm?.notificationType,
        lineLinkSnapshot.status === "linked",
        canUseNewsEmail
      ),
      email:
        typeof routeState.newsJobForm?.email === "string"
          ? routeState.newsJobForm.email
          : (storage.getNewsEmail() ?? ""),
      mode: routeState.newsJobForm?.mode === "report" ? "report" : "news",
      readingProfile:
        routeState.newsJobForm?.readingProfile === "grasp" ||
        routeState.newsJobForm?.readingProfile === "organize" ||
        routeState.newsJobForm?.readingProfile === "think"
          ? routeState.newsJobForm.readingProfile
          : INITIAL_NEWS_JOB_FORM.readingProfile,
      deliveryOption: resolveNewsDeliveryOption(routeState.newsJobForm?.deliveryOption),
    });
    const pendingSnapshot = readPendingNewsJobSnapshot();
    if (pendingSnapshot) {
      setPendingNewsJob(pendingSnapshot);
      beginNewsJobTracking(pendingSnapshot);
      void refreshNewsJobStatus(pendingSnapshot.jobId);
    }
    void refreshNewsEmailIntegrationStatus();
    setNewsJobModalOpen(true);

    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  // refreshNewsJobStatus は毎レンダーで再生成されるため依存に含めない
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canUseNewsEmail,
    lineLinkSnapshot.status,
    location.pathname,
    location.search,
    location.state,
    navigate,
    refreshNewsEmailIntegrationStatus,
  ]);

  useEffect(() => {
    const syncPendingNewsJob = () => {
      setPendingNewsJob(readPendingNewsJobSnapshot());
    };

    syncPendingNewsJob();
    window.addEventListener(NEWS_PENDING_JOB_EVENT, syncPendingNewsJob as EventListener);

    return () => {
      window.removeEventListener(NEWS_PENDING_JOB_EVENT, syncPendingNewsJob as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!newsJobModalOpen) return;
    void refreshNewsEmailIntegrationStatus();
  }, [newsJobModalOpen, refreshNewsEmailIntegrationStatus]);

  useEffect(() => {
    if (pendingNewsJob) return;

    setNewsJobForm((prev) => {
      if (prev.notificationType === "line" && lineLinkSnapshot.status !== "linked") {
        return {
          ...prev,
          notificationType: getDefaultNewsNotificationType(false, canUseNewsEmail),
        };
      }

      if (prev.notificationType === "email" && !canUseNewsEmail) {
        return {
          ...prev,
          notificationType: getDefaultNewsNotificationType(
            lineLinkSnapshot.status === "linked",
            false
          ),
        };
      }

      if (prev.notificationType === "line" && prev.deliveryOption !== "text") {
        return {
          ...prev,
          deliveryOption: "text",
        };
      }

      return prev;
    });
  }, [canUseNewsEmail, lineLinkSnapshot.status, pendingNewsJob]);

  useEffect(() => {
    voiceActiveRef.current = voiceActive;
  }, [voiceActive]);

  const scrollToEnd = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
      });
    });
  };

  const appendUserBubble = (bubbleText: string) => {
    setMessages((prev) => [
      ...prev,
      { id: makeRuntimeId("u"), from: "user", text: bubbleText, time: nowHHMM(), date: nowYYYYMMDD() },
    ]);
    scrollToEnd();
  };

  const appendAssistantBubble = (assistantText: string, payload?: ChatTalkPayload) => {
    const messageId = payload?.messages?.id;
    const messageDate = payload?.messages?.date ?? undefined;
    setMessages((prev) => [
      ...prev,
      {
        id: messageId === undefined || messageId === null ? makeRuntimeId("a") : String(messageId),
        from: "character",
        text: assistantText,
        time: formatTimeFromDateString(messageDate) || nowHHMM(),
        date: formatDateFromDateString(messageDate) || nowYYYYMMDD(),
      },
    ]);
    scrollToEnd();
  };

  const stopSpeechAudio = () => {
    const current = speechAudioRef.current;
    if (!current) return;
    current.pause();
    current.currentTime = 0;
    speechAudioRef.current = null;
  };

  const playSpeechAudio = async (url?: string | null) => {
    const trimmedUrl = (url ?? "").trim();
    if (!trimmedUrl) return;

    // 音声出力が無効の場合はスキップ
    if (!speechEnabled) return;

    try {
      stopSpeechAudio();
      const audio = new Audio(trimmedUrl);
      speechAudioRef.current = audio;
      await audio.play();
    } catch {
      // autoplay制限や再生失敗時は無視（テキスト表示は継続）
    }
  };

  const extractAssistantTextFromResponse = (res: ChatTalkPayload | ChatTalkEnvelope | null | undefined) => {
    const payload = normalizeChatTalkPayload(res);
    const assistantText = payload?.messages?.text ?? payload?.message ?? "";
    return { payload, assistantText };
  };

  const refreshHeaderFromUser = async (): Promise<{ user: UserData; characterId: string | null }> => {
    const [user, accountStateResult, googleIntegrationResult] = await Promise.all([
      api.getUser(),
      api.getMe().then(
        (accountState) => ({ ok: true as const, accountState }),
        (err) => { logError(err); return { ok: false as const }; }
      ),
      api.getGoogleIntegrationStatus(NEWS_GMAIL_SEND_SCOPE_BUNDLE).then(
        (status) => ({ ok: true as const, status }),
        (err) => { logError(err); return { ok: false as const }; }
      ),
    ]);

    const characterId = resolveCurrentCharacterId(user) ?? storage.getAuth().currentCharacterId ?? null;
    storage.setAuth({ currentCharacterId: characterId ?? undefined });

    if (accountStateResult.ok) {
      const nextLineLinkSnapshot = resolveLineLinkSnapshotFromAccountState(
        accountStateResult.accountState,
        readLineLinkSnapshot()
      );
      setLineLinkSnapshot(nextLineLinkSnapshot);
      writeLineLinkSnapshot(nextLineLinkSnapshot);
    } else {
      setLineLinkSnapshot(readLineLinkSnapshot());
    }

    if (googleIntegrationResult.ok) {
      setNewsGoogleIntegrationView(normalizeGoogleIntegrationStatus(googleIntegrationResult.status));
    } else {
      setNewsGoogleIntegrationView(createErrorGoogleIntegrationView());
    }

    const cName =
      user?.currentCharacter?.characterName ?? (user as any)?.characterName ?? "";
    setCharacterName(typeof cName === "string" ? cName : "");

    // iconImageを複数のパスから取得（currentCharacter内を優先）
    const iconImage = pickStringByPaths(user, [
      "currentCharacter.iconImage",
      "data.currentCharacter.iconImage",
      "iconImage",
      "data.iconImage",
    ]);

    const resolvedIconUrl = resolveBackendAssetUrl(iconImage);
    if (resolvedIconUrl) {
      setCharacterIconUrl(resolvedIconUrl);
    } else {
      setCharacterIconUrl("/img/no-image.png");
    }

    if (user.email) {
      setNewsJobForm((prev) => ({ ...prev, email: prev.email || user.email || "" }));
    }

    return { user, characterId };
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setLoadError("");
        didInitialScrollRef.current = false;

        await refreshHeaderFromUser();

        const historyResult = await api.getChatHistory({ limit: CHAT_HISTORY_PAGE_SIZE });
        const initialMessages = mapHistoriesToMessages(historyResult.histories);
        const initialBeforeSeq = historyResult.nextBeforeSeq ?? getOldestMessageSeq(initialMessages);
        setMessages(initialMessages);
        setHasMoreHistory(historyResult.hasMore && initialBeforeSeq !== null);
        setNextBeforeSeq(initialBeforeSeq);
        setLoadOlderError("");
      } catch (e: unknown) {
        if (e instanceof AppError && e.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }
        setHasMoreHistory(false);
        setNextBeforeSeq(null);
        setLoadOlderError("");
        setLoadError(e instanceof Error ? e.message : "ロードに失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // 初回ロード後、履歴があれば末尾へ（フッターに隠れない）
  useEffect(() => {
    if (loading) return;
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }
    if (didInitialScrollRef.current) return;
    if (messages.length === 0) return;
    didInitialScrollRef.current = true;
    scrollToEnd();
  }, [loading, messages]);

  useLayoutEffect(() => {
    if (historyScrollRestoreTick === 0) return;

    const snapshot = historyScrollSnapshotRef.current;
    historyScrollSnapshotRef.current = null;
    const container = scrollContainerRef.current;
    if (!snapshot || !container) return;

    container.scrollTop = container.scrollHeight - snapshot.scrollHeight + snapshot.scrollTop;
  }, [historyScrollRestoreTick]);

  const handleLoadOlderHistory = useCallback(async () => {
    if (loading || isLoadingOlder || !hasMoreHistory) return;

    const beforeSeq = nextBeforeSeq ?? getOldestMessageSeq(messages);
    if (beforeSeq === null) {
      setHasMoreHistory(false);
      setNextBeforeSeq(null);
      return;
    }

    const container = scrollContainerRef.current;
    const snapshot = container
      ? { scrollHeight: container.scrollHeight, scrollTop: container.scrollTop }
      : null;

    try {
      setIsLoadingOlder(true);
      setLoadOlderError("");

      const historyResult = await api.getChatHistory({
        limit: CHAT_HISTORY_PAGE_SIZE,
        beforeSeq,
      });
      const olderMessages = mapHistoriesToMessages(historyResult.histories);
      const existingIds = new Set(messages.map((message) => message.id));
      const uniqueOlderMessages = olderMessages.filter((message) => !existingIds.has(message.id));

      if (uniqueOlderMessages.length > 0) {
        skipNextAutoScrollRef.current = true;
      }

      if (snapshot && uniqueOlderMessages.length > 0) {
        historyScrollSnapshotRef.current = snapshot;
      }

      if (uniqueOlderMessages.length > 0) {
        setMessages((prev) => [...uniqueOlderMessages, ...prev]);
      }

      if (snapshot && uniqueOlderMessages.length > 0) {
        setHistoryScrollRestoreTick((tick) => tick + 1);
      }

      const resolvedNextBeforeSeq =
        historyResult.nextBeforeSeq ?? getOldestMessageSeq(olderMessages) ?? beforeSeq;
      const canLoadAnotherPage =
        historyResult.hasMore && (historyResult.nextBeforeSeq !== null || olderMessages.length > 0);
      setNextBeforeSeq(resolvedNextBeforeSeq);
      setHasMoreHistory(canLoadAnotherPage && resolvedNextBeforeSeq !== null);
    } catch (e: unknown) {
      if (e instanceof AppError && e.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      logError(e);
      setLoadOlderError("過去の会話を読み込めませんでした");
    } finally {
      setIsLoadingOlder(false);
    }
  }, [hasMoreHistory, isLoadingOlder, loading, messages, navigate, nextBeforeSeq]);

  // 画面表示後 / 送信後に入力できる状態になったらフォーカス
  useEffect(() => {
    if (loading) return;
    if (sending) return;
    requestAnimationFrame(() => {
      messageRef.current?.focus();
    });
  }, [loading, sending]);

  // モバイルのキーボード表示で 100vh が暴れるのを避ける（iOS/Android対策）
  useEffect(() => {
    const setAppHeight = () => {
      const nextHeight = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty("--app-height", `${Math.round(nextHeight)}px`);

      if (document.activeElement === messageRef.current) {
        requestAnimationFrame(() => {
          endRef.current?.scrollIntoView({ block: "end" });
        });
      }
    };
    setAppHeight();

    const viewport = window.visualViewport;
    window.addEventListener("resize", setAppHeight);
    viewport?.addEventListener("resize", setAppHeight);
    viewport?.addEventListener("scroll", setAppHeight);

    return () => {
      window.removeEventListener("resize", setAppHeight);
      viewport?.removeEventListener("resize", setAppHeight);
      viewport?.removeEventListener("scroll", setAppHeight);
    };
  }, []);

  const stopNewsJobPolling = () => {
    if (newsJobPollTimerRef.current !== null) {
      window.clearTimeout(newsJobPollTimerRef.current);
      newsJobPollTimerRef.current = null;
    }
  };

  const applyNewsJobStatus = (response: NewsJobStatusData) => {
    const nextPhase: NewsJobViewStatus =
      response.status === "completed"
        ? "completed"
        : response.status === "failed"
          ? "failed"
          : "polling";

    if (response.status !== "accepted") {
      stopNewsJobPolling();
      const pendingSnapshot = readPendingNewsJobSnapshot();
      clearPendingNewsJobSnapshot();
      const notification = toPendingNewsJobNotification(response, {
        mode: pendingSnapshot?.mode,
      });
      if (notification) {
        writePendingNewsJobNotification(notification);
      }
    }

    setNewsJobResult((prev) => ({
      phase: nextPhase,
      id: response.id,
      jobId: response.jobId,
      status: response.status,
      keyword: response.keyword,
      notificationType: response.notificationType,
      acceptedAt: response.acceptedAt ?? prev?.acceptedAt ?? null,
      processedAt: response.processedAt ?? null,
      failedStage: response.failedStage ?? null,
      errorMessage: response.errorMessage ?? null,
      callbackMessage: response.callbackMessage ?? null,
      summary: response.summary ?? null,
    }));
    return response.status;
  };

  const beginNewsJobTracking = (snapshot: PendingNewsJobSnapshot) => {
    const startedAtMs = Date.parse(snapshot.startedAt);
    newsJobStartedAtRef.current = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    setNewsJobResult({
      phase: "accepted",
      id: snapshot.id,
      jobId: snapshot.jobId,
      status: "accepted",
      keyword: snapshot.keyword,
      notificationType: snapshot.notificationType,
      acceptedAt: snapshot.acceptedAt ?? null,
      processedAt: null,
      failedStage: null,
      errorMessage: null,
    });
  };

  const scheduleNewsJobPoll = (jobId: string, timeoutMs: number = NEWS_JOB_POLL_TIMEOUT_MS) => {
    stopNewsJobPolling();

    const startedAtMs = newsJobStartedAtRef.current ?? Date.now();
    if (Date.now() - startedAtMs >= timeoutMs) {
      setNewsJobResult((prev) => (prev ? { ...prev, phase: "timeout" } : prev));
      return;
    }

    newsJobPollTimerRef.current = window.setTimeout(async () => {
      setNewsJobRefreshing(true);
      setNewsJobError(null);

      try {
        const response = await api.getNewsJob(jobId);
        const status = applyNewsJobStatus(response);

        if (status === "accepted") {
          scheduleNewsJobPoll(jobId, timeoutMs);
        }
      } catch (error) {
        logError(error);
        stopNewsJobPolling();
        setNewsJobError({
          message: "ニュースジョブの状態を確認できませんでした。",
          detail: extractBackendMessage(error),
        });
        setNewsJobResult((prev) => (prev ? { ...prev, phase: "timeout" } : prev));
      } finally {
        setNewsJobRefreshing(false);
      }
    }, NEWS_JOB_POLL_INTERVAL_MS);
  };

  const refreshNewsJobStatus = async (jobIdOverride?: string) => {
    const jobId = jobIdOverride ?? newsJobResult?.jobId;
    if (!jobId) {
      setNewsJobError({ message: "確認するニュースジョブが見つかりませんでした。" });
      return;
    }

    setNewsJobRefreshing(true);
    setNewsJobError(null);

    try {
      const response = await api.getNewsJob(jobId);
      const status = applyNewsJobStatus(response);

      if (status === "accepted") {
        scheduleNewsJobPoll(response.jobId);
      }
    } catch (error) {
      logError(error);
      stopNewsJobPolling();
      setNewsJobError({
        message: "ニュースジョブの状態を確認できませんでした。",
        detail: extractBackendMessage(error),
      });
      setNewsJobResult((prev) => (prev ? { ...prev, phase: "timeout" } : prev));
    } finally {
      setNewsJobRefreshing(false);
    }
  };

  const closeNewsJobModal = () => {
    if (newsJobSubmitting) return;
    setNewsJobModalOpen(false);
    setNewsJobError(null);
  };

  function updateNewsJobField<K extends keyof NewsJobFormState>(key: K, value: NewsJobFormState[K]) {
    setNewsJobForm((prev) => ({ ...prev, [key]: value }));
    if (!pendingNewsJob) {
      setNewsJobError(null);
      setNewsJobResult(null);
    }
  }

  const openLineLinkFromChat = () => {
    setNewsJobModalOpen(false);
    navigate("/line-link", {
      state: {
        returnTo: "/chat",
        returnState: {
          reopenNewsJobModal: true,
          newsJobForm,
        },
      },
    });
  };

  const openGoogleLinkFromChat = () => {
    setNewsJobModalOpen(false);
    // TODO: 個別スコープ連携(ニュースのみ)が安定したら news に戻す
    navigate(
      `/google-link?returnTo=${encodeURIComponent("/chat")}&mode=single&scopeBundle=${encodeURIComponent(
        GOOGLE_SCOPE_BUNDLES.bundle
      )}`,
      {
        state: {
          returnTo: "/chat",
          mode: "single",
          scopeBundle: GOOGLE_SCOPE_BUNDLES.bundle,
          returnState: {
            reopenNewsJobModal: true,
            newsJobForm,
          },
        },
      }
    );
  };

  const startNewsGoogleReconsent = () => {
    const reconsent = newsJobError?.newsGoogleReconsent;
    if (!reconsent) return;

    const keyword = newsJobForm.keyword.trim();
    const email = newsJobForm.email.trim();
    const wizmateUserId = storage.getAuth().userId?.trim() ?? "";
    const redirectUrl = resolveNewsGoogleReconsentCompleteUrl();

    if (!keyword || !email || !wizmateUserId) {
      setNewsJobError({
        message: "連携の開始に失敗しました。入力内容を確認して再試行してください。",
      });
      return;
    }
    const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(reconsent.authorizationUrl);
    if (!authorizationUrl) {
      setNewsJobError({
        message: "連携URLが不正です。もう一度お試しください。",
      });
      return;
    }

    writePendingNewsGoogleReconsentSnapshot({
      keyword,
      notificationType: "email",
      email,
      wizmateUserId,
      // TODO: 個別スコープ再同意が安定したら requiredScopeBundle を使う
      scopeBundle: GOOGLE_SCOPE_BUNDLES.bundle,
      authorizationUrl,
      redirectUrl,
      createdAt: new Date().toISOString(),
      mode: newsJobForm.mode,
      readingProfile: newsJobForm.mode !== "report" ? newsJobForm.readingProfile : undefined,
      deliveryOption: newsJobForm.notificationType === "line" ? "text" : newsJobForm.deliveryOption,
    });

    window.location.assign(authorizationUrl);
  };

  const submitNewsJob = async () => {
    if (newsJobSubmitting || pendingNewsJob) {
      return;
    }

    const keyword = newsJobForm.keyword.trim();

    if (!keyword) {
      setNewsJobError({ message: "キーワードを入力してください。" });
      return;
    }

    if (newsJobForm.notificationType === "line" && lineLinkSnapshot.status !== "linked") {
      setNewsJobError({ message: "LINE連携を完了してからお試しください。" });
      return;
    }

    const email = newsJobForm.email.trim();
    const redirectUrl = resolveNewsGoogleReconsentCompleteUrl();

    if (newsJobForm.notificationType === "email" && !email) {
      setNewsJobError({ message: "メールアドレスを入力してください。" });
      return;
    }

    if (newsJobForm.notificationType === "email" && !canUseNewsEmail) {
      setNewsJobError({
        message: "Gmail 配信には追加の Google 連携が必要です。",
      });
      return;
    }

    setNewsJobSubmitting(true);
    setNewsJobError(null);
    clearPendingNewsGoogleReconsentSnapshot();

    try {
      const isReport = newsJobForm.mode === "report";
      const pollTimeoutMs = isReport ? REPORT_JOB_POLL_TIMEOUT_MS : NEWS_JOB_POLL_TIMEOUT_MS;

      const response: NewsJobResponse = await api.createNewsJob({
        keyword,
        notificationType: newsJobForm.notificationType,
        ...(newsJobForm.notificationType === "email" ? { email, redirectUrl } : {}),
        mode: newsJobForm.mode,
        ...(!isReport ? { readingProfile: newsJobForm.readingProfile } : {}),
        deliveryOption:
          newsJobForm.notificationType === "line" ? "text" : newsJobForm.deliveryOption,
      });

      if (newsJobForm.notificationType === "email" && email) {
        storage.setNewsEmail(email);
      }

      const pendingSnapshot: PendingNewsJobSnapshot = {
        ...createPendingNewsJobSnapshot(response),
        mode: newsJobForm.mode,
        readingProfile: isReport ? null : newsJobForm.readingProfile,
        deliveryOption:
          newsJobForm.notificationType === "line" ? "text" : newsJobForm.deliveryOption,
      };
      writePendingNewsJobSnapshot(pendingSnapshot);
      setPendingNewsJob(pendingSnapshot);
      beginNewsJobTracking(pendingSnapshot);
      scheduleNewsJobPoll(pendingSnapshot.jobId, pollTimeoutMs);
    } catch (error) {
      logError(error);
      const conflictDisplay = resolveNewsJobConflictDisplay(error);
      if (
        newsJobForm.notificationType === "email" &&
        conflictDisplay?.shouldShowGoogleReconnect &&
        conflictDisplay.connectAuthorizationUrl
      ) {
        const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(
          conflictDisplay.connectAuthorizationUrl
        );
        if (!authorizationUrl) {
          setNewsJobError({
            message: "Google連携URLが不正です。しばらくしてから再試行してください。",
          });
          return;
        }

        clearPendingNewsGoogleReconsentSnapshot();
        setNewsJobError({
          message: "Gmail 配信には追加の Google 連携が必要です。",
          detail: conflictDisplay.detail,
          newsGoogleReconsent: {
            authorizationUrl,
            // TODO: 個別スコープ再同意が安定したら conflictDisplay.requiredScopeBundle を優先する
            requiredScopeBundle: conflictDisplay.requiredScopeBundle ?? GOOGLE_SCOPE_BUNDLES.bundle,
          },
        });
        return;
      }

      setNewsJobError({
        message: conflictDisplay?.message ?? toUserMessage(error),
        detail: conflictDisplay?.detail ?? extractBackendMessage(error),
      });
    } finally {
      setNewsJobSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (newsJobPollTimerRef.current !== null) {
        window.clearTimeout(newsJobPollTimerRef.current);
        newsJobPollTimerRef.current = null;
      }
    };
  }, []);

  const handleSend = async () => {
    await sendTextMessage(text, { clearInput: true });
  };

  const sendTextMessage = async (
    rawText: string,
    opts?: {
      clearInput?: boolean;
    }
  ) => {
    const trimmed = rawText.trim();
    if (!trimmed) return;
    if (sendingRef.current) return;
    if (Date.now() < sendCooldownUntilRef.current) return;

    setSending(true);
    sendingRef.current = true;
    setSendError("");

    appendUserBubble(trimmed);
    if (opts?.clearInput) setText("");

    // タイピングインジケーターを追加
    const typingId = makeRuntimeId("typing");
    setMessages((prev) => [
      ...prev,
      {
        id: typingId,
        from: "character",
        text: "",
        time: "",
        date: nowYYYYMMDD(),
        isTyping: true,
      },
    ]);
    scrollToEnd();

    try {
      // 音声出力状態に応じてspeechTypeを設定（ON=8, OFF=0）
      const res = await api.chatTalk({
        text: trimmed,
        speechType: speechEnabled ? 8 : 0,
      });
      const { payload, assistantText } = extractAssistantTextFromResponse(res);

      if (!assistantText) {
        throw new Error("AI回答が取得できませんでした");
      }

      // タイピングインジケーターを削除してから実際のメッセージを追加
      setMessages((prev) => prev.filter((m) => m.id !== typingId));
      appendAssistantBubble(assistantText, payload);

      // 送信ブロックをここで解除（ヘッダー更新は次のメッセージ送信をブロックしない）
      setSending(false);
      sendingRef.current = false;
      sendCooldownUntilRef.current = Date.now() + SEND_COOLDOWN_MS;

      // 音声再生とヘッダー更新はバックグラウンドで実行
      void playSpeechAudio(payload?.speechFileUrl);
      void refreshHeaderFromUser();
    } catch (e: unknown) {
      // エラー時もタイピングインジケーターを削除
      setMessages((prev) => prev.filter((m) => m.id !== typingId));
      if (e instanceof AppError && e.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      setSendError(toUserMessage(e));
    } finally {
      setSending(false);
      sendingRef.current = false;
      if (voiceActiveRef.current) {
        voiceSessionSentRef.current = false;
        const pending = pendingVoiceTextRef.current;
        if (pending) {
          pendingVoiceTextRef.current = null;
          window.setTimeout(() => {
            sendTextMessage(pending, { clearInput: false });
          }, 0);
        } else {
          // API応答後すぐに音声認識を再開
          const currentRec = voiceRecognitionRef.current;
          if (currentRec) {
            try {
              currentRec.stop();
            } catch {
              // ignore
            }
          }
          window.setTimeout(() => {
            if (voiceActiveRef.current && !voiceRecording) {
              // タイマーをクリアしてから再開
              if (voiceSendTimerRef.current !== null) {
                window.clearTimeout(voiceSendTimerRef.current);
                voiceSendTimerRef.current = null;
              }
              voiceAccumulatedTextRef.current = "";
              startVoiceInput();
            }
          }, 50);
        }
      }
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getSpeechRecognition = () => {
    if (typeof window === "undefined") return null;
    const w = window as any;
    return w.SpeechRecognition || w.webkitSpeechRecognition || null;
  };

  const stopVoiceInput = () => {
    const rec = voiceRecognitionRef.current;
    if (!rec) return;
    try {
      rec.stop();
    } catch {
      // ignore
    }
    voiceRecognitionRef.current = null;
    
    // タイマーもクリア
    if (voiceSendTimerRef.current !== null) {
      window.clearTimeout(voiceSendTimerRef.current);
      voiceSendTimerRef.current = null;
    }
    if (voiceRestartTimeoutRef.current !== null) {
      window.clearTimeout(voiceRestartTimeoutRef.current);
      voiceRestartTimeoutRef.current = null;
    }
    voiceAccumulatedTextRef.current = "";
    voiceLastFinalIndexRef.current = -1;
  };

  const startVoiceInput = () => {
    if (!voiceSupported) return;
    
    // 既存の認識をクリア
    const existingRec = voiceRecognitionRef.current;
    if (existingRec) {
      try {
        existingRec.stop();
      } catch {
        // ignore
      }
    }
    
    if (voiceRestartTimeoutRef.current !== null) {
      window.clearTimeout(voiceRestartTimeoutRef.current);
      voiceRestartTimeoutRef.current = null;
    }
    
    setVoiceError("");
    voiceSessionSentRef.current = false;
    voiceAccumulatedTextRef.current = "";
    voiceLastFinalIndexRef.current = -1;
    voiceSoundActiveRef.current = false;

    const Rec = getSpeechRecognition();
    if (!Rec) {
      setVoiceSupported(false);
      const ua = navigator.userAgent;
      const isIOS = /iPhone|iPad|iPod/.test(ua);
      if (isIOS) {
        setVoiceError("iOSではSafariの最新版が必要です。Appまたはブラウザをアップデートしてください。");
      } else {
        setVoiceError("このブラウザでは音声入力が使えません。");
      }
      return;
    }

    const recognition = new Rec();
    recognition.lang = "ja-JP";
    
    // iOS vs Android で設定を切り替え
    const isIOS = isIOSRef.current;
    recognition.interimResults = !isIOS;
    recognition.continuous = !isIOS;

    // 音声検出の監視（不要な再起動を防ぐため）
    recognition.onsoundstart = () => {
      voiceSoundActiveRef.current = true;
    };

    recognition.onsoundend = () => {
      voiceSoundActiveRef.current = false;
    };

    recognition.onresult = (event: any) => {
      const results = event?.results;
      
      // 最後の結果のインデックスと状態を確認
      const lastIndex = results.length - 1;
      const isFinal = results[lastIndex]?.isFinal ?? false;
      
      // isFinal=trueになったら、新しい確定結果だけを処理
      if (isFinal && lastIndex > voiceLastFinalIndexRef.current) {
        voiceLastFinalIndexRef.current = lastIndex;
        
        // 新しい確定結果のテキストのみを取得（累積せず、このフレーズだけ）
        const newFinalTranscript = (results[lastIndex]?.[0]?.transcript ?? "").trim();
        if (!newFinalTranscript) return;
        
        // 累積せず、新しいテキストのみを保存
        voiceAccumulatedTextRef.current = newFinalTranscript;
        
        // 既存のタイマーをクリア
        if (voiceSendTimerRef.current !== null) {
          window.clearTimeout(voiceSendTimerRef.current);
        }
        
        // iOSでない場合（Android）は1.5秒後に送信
        if (!isIOS) {
          voiceSendTimerRef.current = window.setTimeout(() => {
            const textToSend = voiceAccumulatedTextRef.current.trim();
            
            if (!textToSend) return;
            if (sendingRef.current) {
              if (!pendingVoiceTextRef.current) pendingVoiceTextRef.current = textToSend;
              return;
            }
            if (voiceSessionSentRef.current) return;
            
            // 送信する前にクリア（重複防止）
            voiceAccumulatedTextRef.current = "";
            voiceSendTimerRef.current = null;
            
            const now = Date.now();
            const last = lastVoiceSentRef.current;
            if (last && last.text === textToSend && now - last.at < 1200) return;
            
            lastVoiceSentRef.current = { text: textToSend, at: now };
            voiceSessionSentRef.current = true;
            sendTextMessage(textToSend, { clearInput: false });
          }, 1500);
        } else {
          // iOSの場合は無条件に送信（onendで自動再起動される）
          const textToSend = voiceAccumulatedTextRef.current.trim();
          if (!textToSend) return;
          if (sendingRef.current) {
            if (!pendingVoiceTextRef.current) pendingVoiceTextRef.current = textToSend;
            return;
          }
          if (voiceSessionSentRef.current) return;
          
          voiceAccumulatedTextRef.current = "";
          const now = Date.now();
          const last = lastVoiceSentRef.current;
          if (last && last.text === textToSend && now - last.at < 1200) return;
          
          lastVoiceSentRef.current = { text: textToSend, at: now };
          voiceSessionSentRef.current = true;
          sendTextMessage(textToSend, { clearInput: false });
        }
      }
    };

    recognition.onerror = (event: any) => {
      const code = event?.error ?? "";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setVoiceError("マイクの許可が必要です。");
      } else if (code === "no-speech") {
        setVoiceError("音声が検出できませんでした。");
      } else {
        setVoiceError("音声入力でエラーが発生しました。");
      }
      setVoiceRecording(false);
    };

    recognition.onend = () => {
      setVoiceRecording(false);
      voiceRecognitionRef.current = null;
      
      // iOSの場合は即座に再起動（continuous: falseのため1発話ごとに停止する）
      // Androidの場合はヘルスチェックで必要時のみ再起動
      if (isIOS && voiceActiveRef.current) {
        const hasPendingSend = voiceSendTimerRef.current !== null;
        const isProcessing = sendingRef.current;
        
        if (!hasPendingSend && !isProcessing) {
          voiceRestartTimeoutRef.current = window.setTimeout(() => {
            if (voiceActiveRef.current && !sendingRef.current) {
              startVoiceInput();
            }
          }, 100);
        }
      }
    };

    voiceRecognitionRef.current = recognition;
    setVoiceRecording(true);

    // ヘルスチェック: Androidのみ2秒ごとに認識が停止していないか確認
    // iOSはonendで即座に再起動するためヘルスチェック不要
    if (voiceHealthCheckIntervalRef.current !== null) {
      window.clearInterval(voiceHealthCheckIntervalRef.current);
    }
    
    if (!isIOS) {
      voiceHealthCheckIntervalRef.current = window.setInterval(() => {
        // voiceActiveがfalseなら停止
        if (!voiceActiveRef.current) {
          if (voiceHealthCheckIntervalRef.current !== null) {
            window.clearInterval(voiceHealthCheckIntervalRef.current);
            voiceHealthCheckIntervalRef.current = null;
          }
          return;
        }
        
        // 認識が停止している場合のみ再起動
        const currentRec = voiceRecognitionRef.current;
        if (!currentRec) {
          const hasPendingSend = voiceSendTimerRef.current !== null;
          const isProcessing = sendingRef.current;
          
          if (!hasPendingSend && !isProcessing) {
            startVoiceInput();
          }
        }
      }, 2000);
    }

    try {
      recognition.start();
    } catch (e: any) {
      // start()が既に実行中のエラーが出る場合、少し待ってから再試行
      if (e?.message?.includes("already started")) {
        window.setTimeout(() => {
          try {
            recognition.start();
          } catch {
            setVoiceRecording(false);
            setVoiceError("音声入力を開始できませんでした。");
          }
        }, 100);
      } else {
        setVoiceRecording(false);
        setVoiceError("音声入力を開始できませんでした。");
      }
    }
  };

  useEffect(() => {
    return () => {
      stopVoiceInput();
      stopSpeechAudio();
      // 長押しタイマーのクリーンアップ
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 長押し開始（キャラクター名）
  const handleLongPressStart = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
    }
    
    longPressTimerRef.current = window.setTimeout(() => {
      // 3秒長押しで音声出力を切り替え
      const newState = !speechEnabled;
      setSpeechEnabled(newState);
      localStorage.setItem("wizmate_speech_enabled", String(newState));
      
      // フィードバック
      const msg = newState ? "音声出力：ON" : "音声出力：OFF";
      alert(msg);
      
      longPressTimerRef.current = null;
    }, 3000);
  };

  // 長押し終了
  const handleLongPressEnd = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  return (
    <div
      className="bg-[#FDF8F5] text-[#4A3E39] antialiased"
      style={{ height: "var(--app-height, 100vh)" }}
    >
      <div className="h-full min-h-0 flex flex-col overflow-hidden">
        {/* header: fixed（キーボード表示時のレイアウト変化でも動かない） */}
        <header className="fixed top-0 left-0 right-0 z-20 bg-[#FDF8F5]/95 backdrop-blur border-b border-[#EADBC8] pt-[env(safe-area-inset-top)]">
          <div className="mx-auto w-full max-w-md px-4">
            <div className="h-14 flex items-center justify-between">
              <Link
                to="/talk"
                className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]"
              >
                ←戻る
              </Link>

              <div className="flex items-center gap-2">
                <p
                  className="text-sm font-bold tracking-tight text-[#7C5A4E] cursor-pointer select-none"
                  style={{
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                  }}
                  onMouseDown={handleLongPressStart}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={handleLongPressStart}
                  onTouchEnd={handleLongPressEnd}
                  onTouchCancel={handleLongPressEnd}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {characterName || "チャット"}
                </p>
                {speechEnabled && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-[#D1907B]"
                  >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                )}
              </div>

              <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
                WizMate
              </Link>
            </div>
          </div>
        </header>

        {/* fixed header の分だけスペーサー（56px + safe-area） */}
        <div className="shrink-0 h-[calc(56px+env(safe-area-inset-top))]" />

        {/* main が唯一のスクロール領域 */}
        <main ref={scrollContainerRef} className="min-h-0 flex-1 bg-[#FDF8F5] overflow-y-auto">
          <div className="mx-auto w-full max-w-md px-4 py-4 space-y-4">
            {loading ? (
              <div className="text-center text-sm text-[#7B6861]">読み込み中...</div>
            ) : loadError ? (
              <div className="text-center text-sm text-[#7B6861]">{loadError}</div>
            ) : null}

            {!loading && !loadError && (hasMoreHistory || loadOlderError) && (
              <div className="flex flex-col items-center gap-2">
                {hasMoreHistory && (
                  <button
                    type="button"
                    onClick={handleLoadOlderHistory}
                    disabled={isLoadingOlder}
                    aria-busy={isLoadingOlder}
                    className="rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-xs font-bold text-[#7C5A4E] shadow-sm hover:bg-[#FFF8F2] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isLoadingOlder ? "読み込み中..." : "過去の会話を読み込む"}
                  </button>
                )}
                {loadOlderError && (
                  <p className="text-center text-xs text-[#B25B4C]">{loadOlderError}</p>
                )}
              </div>
            )}

            {messages.map((m, idx) => {
              const prevMessage = idx > 0 ? messages[idx - 1] : null;
              const shouldShowDate = !prevMessage || m.date !== prevMessage.date;
              return (
                <React.Fragment key={m.id}>
                  {shouldShowDate && m.date && (
                    <div className="flex items-center justify-center">
                      <span className="rounded-full border border-[#EADBC8] bg-white px-3 py-1 text-[11px] text-[#7B6861]">
                        {new Date(m.date).toLocaleDateString("ja-JP", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                  <MessageRow message={m} characterIconUrl={characterIconUrl} />
                </React.Fragment>
              );
            })}

            <div ref={endRef} className="h-px" />
          </div>
        </main>

        <footer className="shrink-0 border-t border-[#EADBC8] bg-white/70 backdrop-blur pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto w-full max-w-md px-4 py-3">
            {sendError ? (
              <p role="alert" className="mb-2 rounded-[1rem] bg-[#FFF0EB] px-3 py-2 text-xs font-bold text-[#B4533A]">
                {sendError}
              </p>
            ) : null}
            <div className="flex items-stretch gap-2">
              <div className="flex-1">
                <label className="sr-only" htmlFor="message">
                  メッセージ
                </label>
                <textarea
                  ref={messageRef}
                  id="message"
                  rows={1}
                  className="h-12 w-full resize-none rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                  placeholder="メッセージを入力..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={sending}
                  maxLength={MAX_MESSAGE_LENGTH}
                />
              </div>

              <button
                id="send"
                onClick={handleSend}
                disabled={sending || !text.trim()}
                className="h-12 rounded-2xl bg-[#5D4D46] px-4 text-sm font-bold text-white shadow-sm hover:bg-[#4A3E39] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                送信
              </button>

              <button
                type="button"
                onClick={() => {
                  if (voiceActive) {
                    setVoiceActive(false);
                    stopVoiceInput();
                  } else {
                    setVoiceActive(true);
                    startVoiceInput();
                  }
                }}
                disabled={!voiceSupported || sending}
                className={`h-12 w-12 rounded-2xl border shadow-sm hover:bg-[#FEFBF9] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all ${
                  voiceActive
                    ? "bg-[#D1907B] border-[#C17962] text-white animate-pulse"
                    : "bg-white border-[#EADBC8] text-[#5D4D46]"
                }`}
                aria-pressed={voiceActive}
                aria-label="音声入力"
              >
                {voiceActive ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                )}
              </button>
            </div>

            {text.length >= 480 && (
              <p className={`mt-1 text-right text-xs ${text.length >= 570 ? "text-[#AF3B2E]" : "text-[#9C867D]"}`}>
                {MAX_MESSAGE_LENGTH - text.length} 文字残り
              </p>
            )}

            {voiceActive && !sending ? (
              <p className="mt-2 text-xs text-[#D1907B] font-bold flex items-center gap-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="animate-pulse">
                  <circle cx="12" cy="12" r="10" />
                </svg>
                聞いています...
              </p>
            ) : sending && voiceActive ? (
              <p className="mt-2 text-xs text-[#7B6861] font-bold">処理中...</p>
            ) : voiceError ? (
              <p className="mt-2 text-xs text-[#AF3B2E]">{voiceError}</p>
            ) : null}
          </div>
        </footer>
      </div>

      <NewsJobModal
        isOpen={newsJobModalOpen}
        form={newsJobForm}
        lineLinkSnapshot={lineLinkSnapshot}
        canUseNewsEmail={canUseNewsEmail}
        submitting={newsJobSubmitting}
        refreshing={false}
        pendingJob={pendingNewsJob}
        error={newsJobError}
        result={null}
        onClose={closeNewsJobModal}
        onSubmit={submitNewsJob}
        onRefresh={refreshNewsJobStatus}
        onOpenLineLink={openLineLinkFromChat}
        onOpenGoogleLink={openGoogleLinkFromChat}
        onStartNewsGoogleReconsent={startNewsGoogleReconsent}
        onChange={updateNewsJobField}
      />
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyNewsJobModal(props: {
  isOpen: boolean;
  form: NewsJobFormState;
  lineLinkSnapshot: LineLinkSnapshot;
  canUseNewsEmail: boolean;
  submitting: boolean;
  error: InlineError | null;
  result: { id: string; jobId: string; status: string } | null;
  onClose: () => void;
  onSubmit: () => void;
  onOpenLineLink: () => void;
  onOpenGoogleLink: () => void;
  onStartNewsGoogleReconsent?: () => void;
  onChange: <K extends keyof NewsJobFormState>(key: K, value: NewsJobFormState[K]) => void;
}) {
  const {
    isOpen,
    form,
    lineLinkSnapshot,
    canUseNewsEmail,
    submitting,
    error,
    result,
    onClose,
    onSubmit,
    onOpenLineLink,
    onOpenGoogleLink,
    onStartNewsGoogleReconsent = () => {},
    onChange,
  } = props;

  if (!isOpen) return null;

  const canUseLine = lineLinkSnapshot.status === "linked";
  const isSubmitDisabled = submitting || result !== null;
  const guideLines = getNewsRequestGuideLines(lineLinkSnapshot, form.notificationType);
  const pendingJob: PendingNewsJobSnapshot | null = null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center overflow-x-hidden bg-[#2B211D]/45 p-3 sm:p-4">
      <div className="w-full max-w-md overflow-hidden rounded-[2rem] border border-[#EADBC8] bg-[#FFFDFB] shadow-[0_18px_40px_rgba(74,62,57,0.22)]">
        <div className="border-b border-[#EADBC8] bg-white/80 px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-black text-[#7C5A4E]">ニュースを送ってもらう</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitDisabled}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#EADBC8] bg-white text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
          {!canUseLine || !canUseNewsEmail ? (
            <div className="flex justify-end gap-2">
              {!canUseLine ? (
                <button
                  type="button"
                  onClick={onOpenLineLink}
                  disabled={isSubmitDisabled}
                  className="inline-flex items-center justify-center rounded-full bg-[#5F8B57] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  LINEと連携する
                </button>
              ) : null}
              {!canUseNewsEmail ? (
                <button
                  type="button"
                  onClick={onOpenGoogleLink}
                  disabled={isSubmitDisabled}
                  className="inline-flex items-center justify-center rounded-full bg-[#B07A55] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Googleと連携する
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="rounded-[1.5rem] border border-[#E4D5C8] bg-white px-5 py-5">
            <label className="block">
              <span className="mb-2 block text-sm font-bold text-[#5D4D46]">キーワード</span>
              <input
                type="text"
                value={form.keyword}
                onChange={(event) => onChange("keyword", event.target.value)}
                placeholder="例: AI, 生成AI, 半導体"
                className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                disabled={isSubmitDisabled}
                maxLength={100}
              />
            </label>

            <fieldset className="mt-4 block">
              <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">通知種別</legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                    form.notificationType === "line"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${canUseLine && !isSubmitDisabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                >
                  <input
                    type="radio"
                    name="newsNotificationType"
                    value="line"
                    checked={form.notificationType === "line"}
                    onChange={() => onChange("notificationType", "line")}
                    disabled={isSubmitDisabled || !canUseLine}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold">LINE</span>
                </label>

                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                    form.notificationType === "email"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled || !canUseNewsEmail ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsNotificationType"
                    value="email"
                    checked={form.notificationType === "email"}
                    onChange={() => onChange("notificationType", "email")}
                    disabled={isSubmitDisabled || !canUseNewsEmail}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold">メール</span>
                </label>

                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                    form.notificationType === "google_chat"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsNotificationType"
                    value="google_chat"
                    checked={form.notificationType === "google_chat"}
                    onChange={() => onChange("notificationType", "google_chat")}
                    disabled={isSubmitDisabled}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold">{TEST_NEWS_CHAT_LABEL}</span>
                </label>
              </div>
            </fieldset>

            {form.notificationType === "email" ? (
              <label className="mt-4 block">
                <span className="mb-2 block text-sm font-bold text-[#5D4D46]">メールアドレス</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => onChange("email", event.target.value)}
                  placeholder="例: your@example.com"
                  className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                  disabled={isSubmitDisabled || !canUseNewsEmail}
                  maxLength={200}
                />
              </label>
            ) : null}
          </div>

          {error ? (
            <div className="rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
              <p>{error.message}</p>
              {error.detail ? <p className="mt-1 text-xs text-[#B86A5D]">{error.detail}</p> : null}
              {error.newsGoogleReconsent ? (
                <button
                  type="button"
                  onClick={onStartNewsGoogleReconsent}
                  disabled={submitting}
                  className="mt-3 inline-flex items-center justify-center rounded-full bg-[#B07A55] px-4 py-2 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  連携する
                </button>
              ) : null}
            </div>
          ) : null}

          {pendingJob ? (
            <div className="rounded-[1.6rem] border border-[#E4D5C8] bg-[#FFF8F4] px-4 py-4 text-sm text-[#6A4F44]">
              <p className="font-black">ニュースを処理中です。完了または失敗したら、この画面上部とトップ画面の報告でお知らせします。</p>
              <p className="mt-2 leading-6">この画面を閉じても通知されます。</p>
            </div>
          ) : null}

          {result ? (
            <div className="rounded-[1.6rem] border border-[#D8E5D2] bg-[#F4FAF1] px-4 py-4 text-sm text-[#3F5C3B]">
              <p className="font-black">ニュースジョブを受け付けました。</p>
              <dl className="mt-3 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-bold">受付ID</dt>
                  <dd className="break-all text-right">{result.id}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-bold">ステータス</dt>
                  <dd className="text-right">{result.status}</dd>
                </div>
              </dl>
            </div>
          ) : null}

          <div className="rounded-[1.4rem] border border-white/70 bg-white/80 p-4 text-sm leading-6 text-[#6A4F44]">
            {guideLines.map((line, index) => (
              <p key={`${lineLinkSnapshot.status}-${form.notificationType}-${index}`}>{index + 1}. {line}</p>
            ))}
          </div>
        </div>

        <div className="border-t border-[#EADBC8] bg-white/70 px-4 py-4 sm:px-5">
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            className="w-full rounded-[1.4rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-sm transition-all hover:bg-[#4A3E39] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "送信中..." : result ? "依頼済み" : "依頼する"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewsJobModal(props: {
  isOpen: boolean;
  form: NewsJobFormState;
  lineLinkSnapshot: LineLinkSnapshot;
  canUseNewsEmail: boolean;
  submitting: boolean;
  refreshing: boolean;
  pendingJob: PendingNewsJobSnapshot | null;
  error: InlineError | null;
  result: NewsJobView | null;
  onClose: () => void;
  onSubmit: () => void;
  onRefresh: () => void;
  onOpenLineLink: () => void;
  onOpenGoogleLink: () => void;
  onStartNewsGoogleReconsent: () => void;
  onChange: <K extends keyof NewsJobFormState>(key: K, value: NewsJobFormState[K]) => void;
}) {
  const {
    isOpen,
    form,
    lineLinkSnapshot,
    canUseNewsEmail,
    submitting,
    refreshing,
    pendingJob,
    error,
    result,
    onClose,
    onSubmit,
    onRefresh,
    onOpenLineLink,
    onOpenGoogleLink,
    onStartNewsGoogleReconsent,
    onChange,
  } = props;

  const modalBodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pendingJob && !result && !error) return;

    const container = modalBodyRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [error, pendingJob, result]);

  if (!isOpen) return null;

  const canUseLine = lineLinkSnapshot.status === "linked";
  const isSubmitDisabled = submitting || pendingJob !== null;
  const isCloseDisabled = submitting;
  const isLineNotification = form.notificationType === "line";

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto overflow-x-hidden bg-[#2B211D]/45 p-3 sm:items-center sm:p-4">
      <div className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-md flex-col overflow-hidden rounded-[2rem] border border-[#EADBC8] bg-[#FFFDFB] shadow-[0_18px_40px_rgba(74,62,57,0.22)] sm:max-h-[calc(100vh-2rem)]">
        <div className="border-b border-[#EADBC8] bg-white/80 px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-black text-[#7C5A4E]">ニュースを送ってもらう</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isCloseDisabled}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#EADBC8] bg-white text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>

        <div ref={modalBodyRef} className="space-y-4 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {!canUseLine || !canUseNewsEmail ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              {!canUseLine ? (
                <button
                  type="button"
                  onClick={onOpenLineLink}
                  disabled={isSubmitDisabled}
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#5F8B57] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  LINEと連携する
                </button>
              ) : null}
              {!canUseNewsEmail ? (
                <button
                  type="button"
                  onClick={onOpenGoogleLink}
                  disabled={isSubmitDisabled}
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#B07A55] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  Googleと連携する
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="rounded-[1.5rem] border border-[#E4D5C8] bg-white px-5 py-5">
            <fieldset className="mb-4 block">
              <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">モード</legend>
              <div className="grid grid-cols-2 gap-2">
                <label
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-bold shadow-sm transition-colors ${
                    form.mode === "news"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsJobMode"
                    value="news"
                    checked={form.mode === "news"}
                    onChange={() => onChange("mode", "news")}
                    disabled={isSubmitDisabled}
                    className="sr-only"
                  />
                  ニュース
                </label>
                <label
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-bold shadow-sm transition-colors ${
                    form.mode === "report"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsJobMode"
                    value="report"
                    checked={form.mode === "report"}
                    onChange={() => onChange("mode", "report")}
                    disabled={isSubmitDisabled}
                    className="sr-only"
                  />
                  レポート
                </label>
              </div>
            </fieldset>

            <label className="block">
              <span className="mb-2 block text-sm font-bold text-[#5D4D46]">
                {form.mode === "report" ? "調査テーマ" : "キーワード"}
              </span>
              <input
                type="text"
                value={form.keyword}
                onChange={(event) => onChange("keyword", event.target.value)}
                placeholder={form.mode === "report" ? "例: 生成AIの最新動向と市場展望" : "例: AI, 生成AI, 国内ニュース"}
                className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                disabled={isSubmitDisabled}
                maxLength={100}
              />
            </label>

            {form.mode === "news" ? (
              <fieldset className="mt-4 block">
                <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">コンテンツ深度</legend>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      { value: "grasp", label: "把握" },
                      { value: "organize", label: "整理" },
                      { value: "think", label: "思考" },
                    ] as const
                  ).map(({ value, label }) => (
                    <label
                      key={value}
                      className={`flex flex-1 cursor-pointer items-center justify-center rounded-2xl border px-3 py-2 text-sm font-bold shadow-sm transition-colors ${
                        form.readingProfile === value
                          ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#5D4D46]"
                      } ${isSubmitDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                    >
                      <input
                        type="radio"
                        name="newsReadingProfile"
                        value={value}
                        checked={form.readingProfile === value}
                        onChange={() => onChange("readingProfile", value)}
                        disabled={isSubmitDisabled}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}

            <fieldset className="mt-4 block">
              <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">通知先</legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                    form.notificationType === "line"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${canUseLine && !isSubmitDisabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                >
                  <input
                    type="radio"
                    name="newsNotificationType"
                    value="line"
                    checked={form.notificationType === "line"}
                    onChange={() => onChange("notificationType", "line")}
                    disabled={isSubmitDisabled || !canUseLine}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold">LINE</span>
                </label>

                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                    form.notificationType === "email"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled || !canUseNewsEmail ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsNotificationType"
                    value="email"
                    checked={form.notificationType === "email"}
                    onChange={() => onChange("notificationType", "email")}
                    disabled={isSubmitDisabled || !canUseNewsEmail}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold">メール</span>
                </label>

                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                    form.notificationType === "google_chat"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsNotificationType"
                    value="google_chat"
                    checked={form.notificationType === "google_chat"}
                    onChange={() => onChange("notificationType", "google_chat")}
                    disabled={isSubmitDisabled}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold">{TEST_NEWS_CHAT_LABEL}</span>
                </label>
              </div>
            </fieldset>

            <fieldset className="mt-4 block">
              <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">配信オプション</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <label
                  className={`flex cursor-pointer items-center justify-center rounded-2xl border px-3 py-2 text-sm font-bold shadow-sm transition-colors ${
                    form.deliveryOption === "text"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="newsDeliveryOption"
                    value="text"
                    checked={form.deliveryOption === "text"}
                    onChange={() => onChange("deliveryOption", "text")}
                    disabled={isSubmitDisabled}
                    className="sr-only"
                  />
                  テキスト
                </label>

                <label
                  className={`flex items-center justify-center rounded-2xl border px-3 py-2 text-sm font-bold shadow-sm transition-colors ${
                    form.deliveryOption === "audio"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled || isLineNotification ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                >
                  <input
                    type="radio"
                    name="newsDeliveryOption"
                    value="audio"
                    checked={form.deliveryOption === "audio"}
                    onChange={() => onChange("deliveryOption", "audio")}
                    disabled={isSubmitDisabled || isLineNotification}
                    className="sr-only"
                  />
                  音声
                </label>

                <label
                  className={`flex items-center justify-center rounded-2xl border px-3 py-2 text-sm font-bold shadow-sm transition-colors ${
                    form.deliveryOption === "text_and_audio"
                      ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                      : "border-[#EADBC8] bg-white text-[#5D4D46]"
                  } ${isSubmitDisabled || isLineNotification ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                >
                  <input
                    type="radio"
                    name="newsDeliveryOption"
                    value="text_and_audio"
                    checked={form.deliveryOption === "text_and_audio"}
                    onChange={() => onChange("deliveryOption", "text_and_audio")}
                    disabled={isSubmitDisabled || isLineNotification}
                    className="sr-only"
                  />
                  テキスト&音声
                </label>
              </div>
              {isLineNotification ? (
                <p className="mt-2 text-xs text-[#7B6861]">LINE はコスト制約によりテキスト配信のみ選択できます。</p>
              ) : null}
            </fieldset>

            {form.notificationType === "email" ? (
              <label className="mt-4 block">
                <span className="mb-2 block text-sm font-bold text-[#5D4D46]">メールアドレス</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => onChange("email", event.target.value)}
                  placeholder="例: your@example.com"
                  className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                  disabled={isSubmitDisabled || !canUseNewsEmail}
                  maxLength={200}
                />
              </label>
            ) : null}
          </div>

          {error ? (
            <div className="rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
              <p>{error.message}</p>
              {error.detail ? <p className="mt-1 text-xs text-[#B86A5D]">{error.detail}</p> : null}
              {error.newsGoogleReconsent ? (
                <button
                  type="button"
                  onClick={onStartNewsGoogleReconsent}
                  disabled={submitting}
                  className="mt-3 inline-flex items-center justify-center rounded-full bg-[#B07A55] px-4 py-2 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  連携する
                </button>
              ) : null}
            </div>
          ) : null}

          {pendingJob ? (
            <div className="rounded-[1.6rem] border border-[#E4D5C8] bg-[#FFF8F4] px-4 py-4 text-sm text-[#6A4F44]">
              <p className="font-black">
                {pendingJob.mode === "report" ? "レポートを処理中です" : "ニュースを処理中です"}
                。完了または失敗したら、この画面上部とトップ画面の報告でお知らせします。
              </p>
              <p className="mt-2 leading-6">この画面を閉じても通知されます。</p>
              {pendingJob.mode === "report" ? (
                <p className="mt-2 text-xs text-[#8A6A60]">レポートは処理に時間がかかる場合があります。</p>
              ) : null}
            </div>
          ) : null}

          {result ? (
            <div className="rounded-[1.6rem] border border-[#D8E5D2] bg-[#F4FAF1] px-4 py-4 text-sm text-[#3F5C3B]">
              <div className="flex items-start justify-between gap-3">
                <p className="font-black">{getNewsJobHeadline(result)}</p>
                {result.phase === "accepted" || result.phase === "polling" || result.phase === "timeout" ? (
                  <button
                    type="button"
                    onClick={onRefresh}
                    disabled={refreshing}
                    className="shrink-0 rounded-full border border-[#D8E5D2] bg-white px-3 py-1.5 text-xs font-bold text-[#3F5C3B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {refreshing ? "確認中..." : REFRESH_NEWS_JOB_STATUS_BUTTON_LABEL}
                  </button>
                ) : null}
              </div>

              <dl className="mt-3 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-bold">受付ID</dt>
                  <dd className="break-all text-right">{result.id}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-bold">ステータス</dt>
                  <dd className="text-right">{result.status}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-bold">通知先</dt>
                  <dd className="text-right">{getNewsNotificationTypeLabel(result.notificationType)}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-bold">キーワード</dt>
                  <dd className="break-all text-right">{result.keyword}</dd>
                </div>
                {result.acceptedAt ? (
                  <div className="flex items-start justify-between gap-4">
                    <dt className="font-bold">受付時刻</dt>
                    <dd className="text-right">{formatDateTime(result.acceptedAt)}</dd>
                  </div>
                ) : null}
                {result.processedAt ? (
                  <div className="flex items-start justify-between gap-4">
                    <dt className="font-bold">完了時刻</dt>
                    <dd className="text-right">{formatDateTime(result.processedAt)}</dd>
                  </div>
                ) : null}
              </dl>

              {result.phase === "accepted" || result.phase === "polling" ? (
                <div className="mt-4 rounded-[1.2rem] bg-white/70 px-4 py-3 text-[#4D6A48]">
                  ニュースを送る準備を進めています。完了までしばらくお待ちください。
                </div>
              ) : null}

              {result.phase === "completed" ? (
                <div className="mt-4 space-y-3">
                  {result.callbackMessage ? (
                    <div className="rounded-[1.2rem] bg-white/70 px-4 py-3 font-bold text-[#4D6A48]">
                      {result.callbackMessage}
                    </div>
                  ) : (
                    <div className="rounded-[1.2rem] bg-white/70 px-4 py-3 text-[#4D6A48]">
                      選択した通知先へのニュース送信が完了しました。
                    </div>
                  )}
                  {result.summary ? (
                    <details className="rounded-[1.2rem] border border-[#D8E5D2] bg-white/70">
                      <summary className="cursor-pointer px-4 py-3 text-xs font-bold text-[#4D6A48] hover:bg-[#F4FAF1]">
                        サマリーを表示
                      </summary>
                      <div className="border-t border-[#D8E5D2] px-4 py-3">
                        <pre className="whitespace-pre-wrap break-words text-xs leading-6 text-[#3F5C3B]">
                          {result.summary}
                        </pre>
                      </div>
                    </details>
                  ) : null}
                </div>
              ) : null}

              {result.phase === "failed" ? (
                <div className="mt-4 rounded-[1.2rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-[#A1493E]">
                  <p>{toNewsUserFacingErrorMessage(result.errorMessage)}</p>
                  {result.failedStage ? <p className="mt-2 text-xs font-bold">失敗箇所: {result.failedStage}</p> : null}
                </div>
              ) : null}

              {result.phase === "timeout" ? (
                <div className="mt-4 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-3 text-[#7A655C]">
                  状態確認に時間がかかっています。必要に応じて「状態を確認」を押してください。
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="border-t border-[#EADBC8] bg-white/70 px-4 py-4 sm:px-5">
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            className="w-full rounded-[1.4rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-sm transition-all hover:bg-[#4A3E39] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "依頼中..." : pendingJob ? "依頼済み" : "依頼する"}
          </button>
        </div>
      </div>
    </div>
  );
}

const MessageRow = React.memo(function MessageRow({ message, characterIconUrl }: { message: Message; characterIconUrl: string }) {
  const isCharacter = message.from === "character";

  if (isCharacter) {
    return (
      <div className="flex items-end gap-2">
        <img 
          src={characterIconUrl} 
          alt="キャラクター" 
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== "/img/no-image.png") {
              target.src = "/img/no-image.png";
            }
          }}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
        <div className="max-w-[78%]">
          <div className="relative rounded-2xl bg-white border border-[#EADBC8] px-4 py-3 text-sm leading-relaxed shadow-sm text-[#4A3E39]">
            {message.isTyping ? (
              <div className="flex gap-1 items-center py-1">
                <span className="w-2 h-2 rounded-full bg-[#D1A48F] animate-bounce" style={{ animationDelay: "0ms", animationDuration: "1s" }}></span>
                <span className="w-2 h-2 rounded-full bg-[#D1A48F] animate-bounce" style={{ animationDelay: "200ms", animationDuration: "1s" }}></span>
                <span className="w-2 h-2 rounded-full bg-[#D1A48F] animate-bounce" style={{ animationDelay: "400ms", animationDuration: "1s" }}></span>
              </div>
            ) : (
              <ChatRichText text={message.text} />
            )}
            <span className="absolute -left-1 bottom-3 h-3 w-3 rotate-45 bg-white border-l border-b border-[#EADBC8]" />
          </div>
          {!message.isTyping && <p className="mt-1 text-[11px] text-[#9C867D]">{message.time}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-end justify-end gap-2">
      <div className="max-w-[78%] text-right">
        <div className="relative inline-block rounded-2xl bg-[#5D4D46] text-white px-4 py-3 text-sm leading-relaxed shadow-sm">
          <ChatRichText text={message.text} />
          <span className="absolute -right-1 bottom-3 h-3 w-3 rotate-45 bg-[#5D4D46]" />
        </div>
        <p className="mt-1 text-[11px] text-[#9C867D]">{message.time}</p>
      </div>
    </div>
  );
});
